import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePathLync } from "@/pathlync/PathLyncAuth";import { ptApi, ptError } from "@/pathlync/api";
import { PathLyncShell, StatCard, StatusPill } from "@/pathlync/PathLyncShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Loader2, FileText, Clock } from "lucide-react";

const blank = {
  patient_reference: "", specimen_type: "", age_sex: "", collected_on: "",
  clinical_history: "", question: "", priority: "Routine",
};

const LabDashboard = () => {
  const { user } = usePathLync();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([ptApi.get("/consults"), ptApi.get("/stats")]);
      setRows(list.data);
      setStats(s.data);
    } catch (e) { toast.error(ptError(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await ptApi.post("/consults", form);
      toast.success(`Consultation ${data.reference} created — add images next`);
      setOpen(false);
      setForm(blank);
      await load();
      navigate(`/pathlync/consults/${data.id}`);
    } catch (e) { toast.error(ptError(e)); } finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <PathLyncShell>
      <div className="flex flex-wrap items-end gap-3" data-testid="lab-dashboard">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Consultations</h1>
          <p className="text-sm text-slate-600">
            {user.institution || "Your laboratory"} — requests you have sent and reports received.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="new-consult-button"
                className="ml-auto bg-[#1e3a8a] hover:bg-[#172f6e]">
          <Plus size={16} className="mr-2" /> New consultation
        </Button>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Drafts" value={stats.draft ?? 0} testid="stat-draft" tone="text-slate-700"
                  loading={loading} />
        <StatCard label="Awaiting opinion" value={stats.awaiting ?? 0} testid="stat-awaiting"
                  loading={loading} />
        <StatCard label="Reports received" value={stats.reported ?? 0} testid="stat-reported"
                  tone="text-emerald-700" loading={loading} />
        <StatCard label="Urgent open" value={stats.urgent ?? 0} testid="stat-urgent"
                  tone="text-red-700" loading={loading} />
      </div>

      {loading && <Loader2 className="mt-8 animate-spin text-slate-400" size={20} />}

      {!loading && rows.length === 0 && (
        <p className="mt-8 border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500"
           data-testid="lab-consults-empty">
          No consultations yet. Start one and attach your gross and microscopic images.
        </p>
      )}

      <div className="mt-6 space-y-3">
        {rows.map((r) => (
          <Link key={r.id} to={`/pathlync/consults/${r.id}`} data-testid={`consult-row-${r.id}`}
                className="block border border-slate-200 bg-white p-4 transition-colors hover:border-[#1e3a8a]">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm font-semibold text-[#1e3a8a]">{r.reference}</span>
              <StatusPill status={r.status} />
              {r.priority === "Urgent" && (
                <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
                  Urgent
                </span>
              )}
              <span className="ml-auto text-xs text-slate-500">
                {r.image_count} image{r.image_count === 1 ? "" : "s"}
              </span>
            </div>
            <p className="mt-2 text-sm font-semibold text-slate-900">{r.specimen_type}</p>
            <p className="text-sm text-slate-600">Patient ref {r.patient_reference}</p>
            <p className="mt-1 line-clamp-1 text-sm italic text-slate-500">{r.question}</p>
            <p className="mt-2 flex items-center gap-4 text-xs text-slate-500">
              {r.pathologist_name && <span>Reviewed by {r.pathologist_name}</span>}
              {r.status === "Reported"
                ? <span className="inline-flex items-center gap-1 font-semibold text-emerald-700">
                    <FileText size={12} /> Report ready
                  </span>
                : <span className="inline-flex items-center gap-1">
                    <Clock size={12} /> {new Date(r.created_at).toLocaleDateString()}
                  </span>}
            </p>
          </Link>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="new-consult-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">New consultation request</DialogTitle>
            <DialogDescription>
              Use a patient reference rather than a name. You will attach images on the next screen.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="c-ref">Patient reference</Label>
                <Input id="c-ref" required value={form.patient_reference}
                       onChange={set("patient_reference")} data-testid="consult-patient-input"
                       placeholder="S26-01188 or MRN" />
              </div>
              <div>
                <Label htmlFor="c-age">Age / sex</Label>
                <Input id="c-age" value={form.age_sex} onChange={set("age_sex")}
                       data-testid="consult-age-input" placeholder="62 F" />
              </div>
            </div>
            <div>
              <Label htmlFor="c-spec">Specimen</Label>
              <Input id="c-spec" required value={form.specimen_type} onChange={set("specimen_type")}
                     data-testid="consult-specimen-input" placeholder="Colon, right hemicolectomy" />
            </div>
            <div>
              <Label htmlFor="c-hist">Clinical history</Label>
              <Textarea id="c-hist" rows={3} value={form.clinical_history}
                        onChange={set("clinical_history")} data-testid="consult-history-input"
                        placeholder="Relevant history, imaging, prior biopsies…" />
            </div>
            <div>
              <Label htmlFor="c-q">Question for the consultant</Label>
              <Textarea id="c-q" rows={2} required value={form.question} onChange={set("question")}
                        data-testid="consult-question-input"
                        placeholder="Confirm invasion? Grade? Second opinion on margin?" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="c-pri">Priority</Label>
                <select id="c-pri" value={form.priority} onChange={set("priority")}
                        data-testid="consult-priority-select"
                        className="mt-1 h-10 w-full border border-slate-300 bg-white px-3 text-sm">
                  <option>Routine</option>
                  <option>Urgent</option>
                </select>
              </div>
              <div>
                <Label htmlFor="c-col">Collected on</Label>
                <Input id="c-col" type="date" value={form.collected_on} onChange={set("collected_on")}
                       data-testid="consult-collected-input" />
              </div>
            </div>
            <Button type="submit" disabled={busy} data-testid="create-consult-submit"
                    className="w-full bg-[#1e3a8a] hover:bg-[#172f6e]">
              {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
              Create and add images
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </PathLyncShell>
  );
};

export default LabDashboard;
